import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  EventType,
  FlashMapComponent,
  FlashMapNodeModel,
  FlashMapService,
  getFlashMapTranslations,
} from 'flash-map';
import {
  MINDMAP_THEME_PALETTES,
  type MindmapVisualTheme,
} from '../../mindmap-theme-palettes';
import { UiTranslationService } from '@core/services/ui-translation.service';
import { MindmapTranslations } from '../../mindmap-translations';

type NodeActionItem =
  | 'associate'
  | 'refine'
  | 'train'
  | 'deck'
  | 'note'
  | 'image'
  | 'style'
  | 'text';
type NodeImagePosition = 'top' | 'right' | 'bottom' | 'left';
type NodeRefineAction = 'add-notes' | 'expand-content' | 'shorten-text';
type StyleTab = 'shape' | 'border' | 'line';
type TextFormat = 'bold' | 'italic';
type FontSizeId = 'small' | 'medium' | 'large';
type ShapeId = 'square' | 'rounded-5' | 'rounded-30';
type BorderWidthId = 'thin' | 'medium' | 'thick';
type BorderStyleId = 'solid' | 'dashed' | 'dotted';
type LineWidthId = 'thin' | 'medium' | 'thick';
type LineStyleId = 'solid' | 'dashed' | 'dotted';
type LineTypeId = 'straight' | 'dynamic';
type FontOptionId = 'default' | 'brush-script' | 'comic-sans' | 'serif';

type FlashMapPanelApi = {
  flashMapService?: FlashMapService;
  mode?: () => 'map' | 'outline';
  openNodeNote?: (nodeId: string) => void;
  openFlashcardDeck?: (nodeId: string) => void;
  presentationState?: () => { active: boolean };
};

type NodeViewData = {
  element?: HTMLElement;
};

type PositionState = {
  visible: boolean;
  left: number;
  top: number;
  verticalPlacement: 'above' | 'below';
};

type PopoverPositionState = {
  left: number;
  top: number;
  maxHeight: number | null;
  maxWidth: number | null;
};

type NodeDataRecord = Record<string, unknown> & {
  'leading-line-color'?: string;
  'leading-line-width'?: number;
  'leading-line-style'?: LineStyleId;
  'leading-line-type'?: LineTypeId;
  'font-family'?: string;
  data?: {
    text?: {
      color?: string;
      bold?: boolean;
      italic?: boolean;
    };
  };
  text?: {
    color?: string;
    bold?: boolean;
    italic?: boolean;
  };
};

const DEFAULT_TEXT_COLOR = '#1f2430';
const DEFAULT_BACKGROUND_COLOR = '#ffffff';
const DEFAULT_BORDER_COLOR = '#000000';
const TRANSPARENT_COLOR = 'transparent';
const DEFAULT_FONT_SIZE = 18;
const PANEL_EDGE_INSET = 12;
const PANEL_NODE_GAP = 8;
const POPOVER_GAP = 8;
const MOBILE_BREAKPOINT = 700;
const DEFAULT_FONT_SIZE_SCALE: Record<FontSizeId, number> = {
  small: 14,
  medium: 18,
  large: 22,
};
const CURSIVE_FONT_SIZE_SCALE: Record<FontSizeId, number> = {
  small: 22,
  medium: 24,
  large: 26,
};

@Component({
  selector: 'app-map-node-action-panel',
  templateUrl: './map-node-action-panel.html',
  styleUrl: './map-node-action-panel.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:pointerdown)': 'onDocumentPointerDown($event)',
  },
})
export class MapNodeActionPanelComponent implements OnDestroy {
  private readonly uiTranslation = inject(UiTranslationService);
  protected readonly i18n =
    this.uiTranslation.namespace<MindmapTranslations>('mindmap');

  readonly flashMap = input<FlashMapComponent | null>(null);
  readonly language = input<string | null>(null);
  readonly theme = input<MindmapVisualTheme>('colorful');
  readonly layoutMode = input<'mindmap' | 'org-chart' | 'list'>('mindmap');
  readonly mindMapNotesBlocked = input(false);
  readonly mindMapNotesLimitClick = output<void>();
  readonly trainingModeClick = output<{ nodeId: string }>();
  readonly associateDeckClick = output<{ nodeId: string }>();
  readonly refineNodeClick = output<{
    nodeId: string;
    action: NodeRefineAction;
  }>();
  readonly imagePickerClick = output<{ nodeId: string }>();

  private rafId: number | null = null;
  private popoverRafId: number | null = null;
  private boundService: FlashMapService | null = null;
  private boundContainer: HTMLElement | null = null;
  private readonly imageMenuHandler = (event: Event): void => {
    const detail = (event as CustomEvent<{ nodeId?: string }>).detail;
    const nodeId = detail?.nodeId?.trim();
    const service = this.service();
    if (!nodeId || !service) {
      return;
    }

    service.selectNode(nodeId);
    this.dismissedNodeId.set(null);
    this.popoverPositioned.set(false);
    this.activePopover.set('image');
    this.schedulePopoverPositionUpdate();
  };
  private readonly styleRevision = signal(0);
  private readonly dismissedNodeId = signal<string | null>(null);

  protected readonly activePopover = signal<NodeActionItem | null>(null);
  protected readonly popoverPositioned = signal(false);
  protected readonly activeStyleTab = signal<StyleTab>('shape');
  protected readonly position = signal<PositionState>({
    visible: false,
    left: 0,
    top: 0,
    verticalPlacement: 'above',
  });
  protected readonly popoverPosition = signal<PopoverPositionState>({
    left: 0,
    top: 0,
    maxHeight: null,
    maxWidth: null,
  });

  protected readonly actionItems: readonly NodeActionItem[] = [
    'refine',
    'associate',
    'train',
    'deck',
    'note',
    'image',
    'style',
    'text',
  ];
  protected readonly refineActions: readonly NodeRefineAction[] = [
    'add-notes',
    'expand-content',
    'shorten-text',
  ];
  protected readonly imagePositions: readonly NodeImagePosition[] = [
    'top',
    'right',
    'bottom',
    'left',
  ];

  protected readonly themeColors = computed(() =>
    Array.from(
      new Set([...MINDMAP_THEME_PALETTES[this.theme()], '#000000', '#ffffff']),
    ),
  );

  protected readonly textColors = [
    '#1f2430',
    '#4371f6',
    '#6f7bf7',
    '#f6578c',
    '#f84c76',
    '#f36b2b',
    '#f0c22f',
    '#93d63f',
    '#34d9ce',
    '#18b5c9',
    '#1e88e5',
    '#a179f2',
    '#9c70ed',
    '#2f3a40',
    '#c9d3e5',
    '#ffffff',
  ] as const;

  protected readonly shapeOptions = [
    { id: 'square', radius: '0px' },
    { id: 'rounded-5', radius: '5px' },
    { id: 'rounded-30', radius: '30px' },
  ] as const;

  protected readonly borderWidthOptions = [
    { id: 'thin', value: '1px' },
    { id: 'medium', value: '2px' },
    { id: 'thick', value: '3px' },
  ] as const;

  protected readonly borderStyleOptions = [
    { id: 'solid', value: 'solid' },
    { id: 'dashed', value: 'dashed' },
    { id: 'dotted', value: 'dotted' },
  ] as const;

  protected readonly lineWidthOptions = [
    { id: 'thin', value: '2px' },
    { id: 'medium', value: '4px' },
    { id: 'thick', value: '6px' },
  ] as const;

  protected readonly lineStyleOptions = [
    { id: 'solid', value: 'solid' },
    { id: 'dashed', value: 'dashed' },
    { id: 'dotted', value: 'dotted' },
  ] as const;

  protected readonly lineTypeOptions = [
    { id: 'straight', label: 'Straight' },
    { id: 'dynamic', label: 'Dynamic' },
  ] as const;

  protected readonly fontOptions = [
    {
      id: 'default',
      value: null,
      previewFontFamily: null,
    },
    {
      id: 'brush-script',
      value: "'Dancing Script', 'Segoe Script', 'Lucida Handwriting', cursive",
      previewFontFamily:
        "'Dancing Script', 'Segoe Script', 'Lucida Handwriting', cursive",
    },
    {
      id: 'comic-sans',
      value: "'Comic Sans MS', 'Comic Sans', cursive",
      previewFontFamily: "'Comic Sans MS', 'Comic Sans', cursive",
    },
    {
      id: 'serif',
      value: "'Times New Roman', Times, serif",
      previewFontFamily: "'Times New Roman', Times, serif",
    },
  ] as const;

  protected readonly fontSizeOptions = [
    { id: 'small', label: 'Small', value: DEFAULT_FONT_SIZE_SCALE.small },
    { id: 'medium', label: 'Medium', value: DEFAULT_FONT_SIZE_SCALE.medium },
    { id: 'large', label: 'Large', value: DEFAULT_FONT_SIZE_SCALE.large },
  ] as const;

  protected readonly translations = computed(
    () => getFlashMapTranslations(this.language()).styleToolbar,
  );

  protected readonly service = computed(
    () => this.getPanelApi().flashMapService ?? null,
  );

  protected readonly selectedNode = computed(() => {
    const service = this.service();
    return service?.selectedNode() ?? null;
  });
  protected readonly visibleActionItems = computed(() => {
    const node = this.selectedNode();
    return node?.isroot
      ? this.actionItems.filter(
          (action) =>
            action !== 'associate' && action !== 'train' && action !== 'deck',
        )
      : this.actionItems;
  });

  protected readonly isPresentationActive = computed(
    () => this.getPanelApi().presentationState?.().active === true,
  );

  protected readonly selectedNodeData = computed<NodeDataRecord>(() => {
    this.styleRevision();
    return (this.selectedNode()?.data ?? {}) as NodeDataRecord;
  });
  protected readonly selectedLineColor = computed(() => {
    return this.normalizeColor(
      this.selectedLineConnectionData()['leading-line-color'],
    );
  });
  protected readonly selectedLineWidth = computed<LineWidthId>(() =>
    this.resolveLineWidthId(
      this.selectedLineConnectionData()['leading-line-width'],
    ),
  );
  protected readonly selectedLineStyle = computed<LineStyleId>(() => {
    const style = this.selectedLineConnectionData()['leading-line-style'];
    return style === 'dashed' || style === 'dotted' ? style : 'solid';
  });
  protected readonly selectedLineType = computed<LineTypeId>(() => {
    const type = this.selectedLineConnectionData()['leading-line-type'];
    if (type === 'straight' || type === 'dynamic') {
      return type;
    }

    return this.theme() === 'blast' ? 'straight' : 'dynamic';
  });
  protected readonly canSetLineShape = computed(
    () => this.layoutMode() === 'mindmap' && !this.selectedNode()?.isroot,
  );

  constructor() {
    effect(() => {
      const service = this.service();
      this.bindServiceEvents(service);
      const node = this.selectedNode();
      if (node?.topic && this.isMapMode() && !this.isPresentationActive()) {
        this.startTracking();
      } else {
        this.stopTracking();
        this.hidePanel();
        this.closePopover();
      }
    });
  }

  ngOnDestroy(): void {
    this.stopTracking();
    this.boundContainer?.removeEventListener(
      'flash-map-image-menu',
      this.imageMenuHandler,
    );
    this.boundContainer = null;
    if (this.popoverRafId !== null) {
      cancelAnimationFrame(this.popoverRafId);
    }
  }

  onActionClick(action: NodeActionItem, event: MouseEvent): void {
    event.stopPropagation();
    const node = this.selectedNode();
    if (!node) {
      return;
    }

    if (action === 'deck') {
      this.getPanelApi().openFlashcardDeck?.(node.id);
      this.closePopover();
      return;
    }

    if (action === 'associate') {
      this.associateDeckClick.emit({ nodeId: node.id });
      this.closePopover();
      return;
    }

    if (action === 'train') {
      this.trainingModeClick.emit({ nodeId: node.id });
      this.closePopover();
      return;
    }

    if (action === 'note') {
      if (this.mindMapNotesBlocked()) {
        this.mindMapNotesLimitClick.emit();
        this.closePopover();
        return;
      }

      this.getPanelApi().openNodeNote?.(node.id);
      this.closePopover();
      return;
    }

    if (action === 'image') {
      this.imagePickerClick.emit({ nodeId: node.id });
      this.closePopover();
      return;
    }

    if (this.activePopover() === action) {
      this.closePopover();
      return;
    }

    this.popoverPositioned.set(false);
    this.activePopover.set(action);
    this.schedulePopoverPositionUpdate();
  }

  setImagePosition(position: NodeImagePosition, event: MouseEvent): void {
    event.stopPropagation();
    this.applyToSelectedNode((service, node) =>
      service.setNodeImagePosition(node.id, position),
    );
    this.closePopover();
  }

  removeNodeImage(event: MouseEvent): void {
    event.stopPropagation();
    this.applyToSelectedNode((service, node) =>
      service.setNodeImage(node.id, null),
    );
    this.closePopover();
  }

  onRefineActionClick(action: NodeRefineAction, event: MouseEvent): void {
    event.stopPropagation();
    const nodeId = this.selectedNode()?.id?.trim();
    if (!nodeId) {
      return;
    }

    if (action === 'add-notes' && this.mindMapNotesBlocked()) {
      this.mindMapNotesLimitClick.emit();
      this.closePopover();
      return;
    }

    this.refineNodeClick.emit({
      nodeId,
      action,
    });
    this.closePopover();
  }

  onDocumentPointerDown(event: PointerEvent): void {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || this.eventPathContains(event, '.map-node-action-panel')) {
      return;
    }

    this.closePopover();

    if (this.eventPathContains(event, 'jmnode')) {
      this.dismissedNodeId.set(null);
      this.updatePosition();
      return;
    }

    if (this.eventPathContains(event, 'jmexpander')) {
      return;
    }

    const node = this.selectedNode();
    if (!node) {
      return;
    }

    this.dismissedNodeId.set(node.id);
    this.hidePanel();
  }

  setStyleTab(tab: StyleTab, event: MouseEvent): void {
    event.stopPropagation();
    this.activeStyleTab.set(tab);
    this.schedulePopoverPositionUpdate();
  }

  setLineColor(color: string, event: Event): void {
    event.stopPropagation();
    this.applyToSelectedNode((service, node) =>
      service.setNodeDescendantLineColor(node.id, color),
    );
  }

  resetLineColorsToTheme(event: Event): void {
    event.stopPropagation();
    this.applyToSelectedNode((service, node) =>
      service.resetNodeDescendantLineColorsToTheme(node.id),
    );
  }

  setLineWidth(width: LineWidthId, event: MouseEvent): void {
    event.stopPropagation();
    const option = this.lineWidthOptions.find((item) => item.id === width);
    if (!option) {
      return;
    }
    this.setDescendantLineStyle({ width: Number.parseInt(option.value, 10) });
  }

  setLineStyle(style: LineStyleId, event: MouseEvent): void {
    event.stopPropagation();
    this.setDescendantLineStyle({ style });
  }

  setLineType(type: LineTypeId, event: MouseEvent): void {
    event.stopPropagation();
    this.setDescendantLineStyle({ type });
  }

  isLineColor(color: string): boolean {
    return this.selectedLineColor() === this.normalizeColor(color);
  }

  setBackgroundColor(color: string | null, event: Event): void {
    event.stopPropagation();
    this.applyToSelectedNode((service, node) => {
      if (this.theme() === 'vintage' && this.isVisibleColor(color)) {
        service.setNodeShape(node.id, 'rounded-5');
      }
      service.setNodeStyleColor(node.id, 'background-color', color);
    });
  }

  setThemeBackgroundColor(event: MouseEvent): void {
    event.stopPropagation();
    this.applyToSelectedNode((service, node) => {
      service.setNodeStyleColor(node.id, 'background-color', null);
      if (this.theme() === 'vintage') {
        service.resetNodeShapeToTheme(node.id);
      }
    });
  }

  setNoBackgroundColor(event: MouseEvent): void {
    event.stopPropagation();
    this.setNodeStyleColor('background-color', TRANSPARENT_COLOR);
  }

  setBorderColor(color: string | null, event: Event): void {
    event.stopPropagation();
    this.setNodeStyleColor('border-color', color);
  }

  setThemeBorderColor(event: MouseEvent): void {
    event.stopPropagation();
    this.setNodeStyleColor('border-color', null);
  }

  setNoBorderColor(event: MouseEvent): void {
    event.stopPropagation();
    this.setNodeStyleColor('border-color', TRANSPARENT_COLOR);
  }

  setTextColor(color: string, event: Event): void {
    event.stopPropagation();
    this.setNodeStyleColor('foreground-color', color);
  }

  setFontFamily(fontFamily: string | null, event: MouseEvent): void {
    event.stopPropagation();
    this.applyToSelectedNode((service, node) => {
      const currentLevel = this.resolveFontSizeLevel(
        this.readFontSizeFromNodeData(node.data as NodeDataRecord),
        this.readFontFamilyFromNodeData(node.data as NodeDataRecord),
      );

      service.setNodeStyleValue(node.id, 'font-family', fontFamily);
      service.setNodeFontStyle(
        node.id,
        this.resolveFontSizeByLevel(currentLevel, fontFamily),
        undefined,
        undefined,
      );
    });
  }

  setFontSize(sizeId: FontSizeId, event: MouseEvent): void {
    event.stopPropagation();
    this.applyToSelectedNode((service, node) => {
      service.setNodeFontStyle(
        node.id,
        this.resolveFontSizeByLevel(
          sizeId,
          this.readFontFamilyFromNodeData(node.data as NodeDataRecord),
        ),
        undefined,
        undefined,
      );
    });
  }

  setBorderWidth(value: string | null, event: MouseEvent): void {
    event.stopPropagation();
    this.applyToSelectedNode((service, node) =>
      service.setNodeStyleValue(node.id, 'border-width', value),
    );
  }

  setBorderStyle(value: string | null, event: MouseEvent): void {
    event.stopPropagation();
    this.applyToSelectedNode((service, node) =>
      service.setNodeStyleValue(node.id, 'border-style', value),
    );
  }

  setShape(shape: ShapeId | null, event: MouseEvent): void {
    event.stopPropagation();
    this.applyToSelectedNode((service, node) =>
      service.setNodeShape(node.id, shape),
    );
  }

  toggleBold(event: MouseEvent): void {
    event.stopPropagation();
    const next = this.isBold() ? null : 'bold';
    this.applyToSelectedNode((service, node) =>
      service.setNodeFontStyle(node.id, undefined, next, undefined),
    );
  }

  toggleItalic(event: MouseEvent): void {
    event.stopPropagation();
    const next = this.isItalic() ? null : 'italic';
    this.applyToSelectedNode((service, node) =>
      service.setNodeFontStyle(node.id, undefined, undefined, next),
    );
  }

  isBackgroundColor(color: string | null): boolean {
    const data = this.selectedNodeData();
    const current = this.normalizeColor(data['background-color']);
    return color === null ? !current : current === color.toLowerCase();
  }

  isThemeBackgroundColor(): boolean {
    return !this.normalizeColor(this.selectedNodeData()['background-color']);
  }

  isNoBackgroundColor(): boolean {
    return this.selectedNodeData()['background-color'] === TRANSPARENT_COLOR;
  }

  isBorderColor(color: string | null): boolean {
    const data = this.selectedNodeData();
    const current = this.normalizeColor(data['border-color']);
    return color === null ? !current : current === color.toLowerCase();
  }

  isThemeBorderColor(): boolean {
    return !this.normalizeColor(this.selectedNodeData()['border-color']);
  }

  isNoBorderColor(): boolean {
    return this.selectedNodeData()['border-color'] === TRANSPARENT_COLOR;
  }

  isTextColor(color: string): boolean {
    return this.currentTextColor() === color.toLowerCase();
  }

  setLineShape(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.canSetLineShape()) {
      return;
    }
    this.applyToSelectedNode((service, node) =>
      service.setNodeLineShape(node.id, 'underline'),
    );
  }

  isFontFamily(fontFamily: string | null): boolean {
    return (
      this.normalizeFontFamily(this.currentFontFamily()) ===
      this.normalizeFontFamily(fontFamily)
    );
  }

  isShape(shape: ShapeId | null): boolean {
    const data = this.selectedNodeData();
    const radius =
      typeof data['border-radius'] === 'string' ? data['border-radius'] : null;
    if (shape === null) {
      return !radius;
    }
    return (
      this.shapeOptions.find((option) => option.id === shape)?.radius ===
      this.normalizeShapeRadius(radius)
    );
  }

  private normalizeShapeRadius(radius: string | null): string | null {
    switch (radius) {
      case '10px':
        return '5px';
      case '999px':
        return '30px';
      default:
        return radius;
    }
  }

  isBorderWidth(value: string | null): boolean {
    const current = this.selectedNodeData()['border-width'];
    return value === null ? typeof current !== 'string' : current === value;
  }

  isBorderStyle(value: string | null): boolean {
    const current = this.selectedNodeData()['border-style'];
    return value === null ? typeof current !== 'string' : current === value;
  }

  isBold(): boolean {
    const data = this.selectedNodeData();
    const textData = this.resolveTextData(data);
    return data['font-weight'] === 'bold' || textData?.bold === true;
  }

  isItalic(): boolean {
    const data = this.selectedNodeData();
    const textData = this.resolveTextData(data);
    return data['font-style'] === 'italic' || textData?.italic === true;
  }

  currentBackgroundColor(): string {
    return (
      this.normalizeColor(this.selectedNodeData()['background-color']) ??
      DEFAULT_BACKGROUND_COLOR
    );
  }

  currentBorderColor(): string {
    return (
      this.normalizeColor(this.selectedNodeData()['border-color']) ??
      DEFAULT_BORDER_COLOR
    );
  }

  currentTextColor(): string {
    const data = this.selectedNodeData();
    const textData = this.resolveTextData(data);
    return (
      this.normalizeColor(data['foreground-color']) ??
      this.normalizeColor(textData?.color) ??
      DEFAULT_TEXT_COLOR
    );
  }

  isLineShape(): boolean {
    return this.selectedNodeData()['line-shape'] === 'underline';
  }

  isFontSize(sizeId: FontSizeId): boolean {
    return this.currentFontSizeLevel() === sizeId;
  }

  currentFontFamily(): string | null {
    const fontFamily = this.selectedNodeData()['font-family'];
    return typeof fontFamily === 'string' && fontFamily.trim()
      ? fontFamily.trim()
      : null;
  }

  currentFontSize(): number {
    return this.readFontSizeFromNodeData(this.selectedNodeData());
  }

  currentFontSizeLevel(): FontSizeId {
    return this.resolveFontSizeLevel(
      this.currentFontSize(),
      this.currentFontFamily(),
    );
  }

  labelForAction(action: NodeActionItem): string {
    const labels = this.translations();
    switch (action) {
      case 'associate':
        return this.i18n.t().associateDeck;
      case 'train':
        return 'Choose training mode';
      case 'refine':
        return 'Refine with AI';
      case 'deck':
        return labels.decks;
      case 'note':
        return labels.addNote;
      case 'image':
        return 'Add image';
      case 'style':
        return labels.style;
      case 'text':
        return 'Text Style';
    }
  }

  iconForAction(action: NodeActionItem): string {
    switch (action) {
      case 'associate':
        return '/assets/icons/sprite.svg#link';
      case 'train':
        return '/assets/icons/sprite.svg#play';
      case 'refine':
        return '/assets/icons/sprite.svg#ai-sparkles';
      case 'deck':
        return '/assets/icons/sprite.svg#book-open';
      case 'note':
        return '/assets/icons/sprite.svg#file-text';
      case 'image':
        return '/assets/icons/sprite.svg#image';
      case 'style':
        return '/assets/icons/sprite.svg#droplet';
      case 'text':
        return '/assets/icons/sprite.svg#type';
    }
  }

  iconForFormat(format: TextFormat): string {
    switch (format) {
      case 'bold':
        return '/assets/icons/sprite.svg#bold';
      case 'italic':
        return '/assets/icons/sprite.svg#italic';
    }
  }

  labelForShape(shape: ShapeId | null): string {
    switch (shape) {
      case null:
        return 'Default';
      case 'square':
        return 'Square shape';
      case 'rounded-5':
        return 'Rounded shape';
      case 'rounded-30':
        return 'Pill shape';
    }
  }
  labelForBorderWidth(id: BorderWidthId): string {
    const labels = this.translations();
    switch (id) {
      case 'thin':
        return labels.thin;
      case 'medium':
        return labels.medium;
      case 'thick':
        return labels.thick;
    }
  }

  labelForBorderStyle(id: BorderStyleId): string {
    const labels = this.translations();
    switch (id) {
      case 'solid':
        return labels.solid;
      case 'dashed':
        return labels.dashed;
      case 'dotted':
        return labels.dotted;
    }
  }

  labelForRefineAction(action: NodeRefineAction): string {
    switch (action) {
      case 'add-notes':
        return 'Add notes';
      case 'expand-content':
        return 'Expand Content';
      case 'shorten-text':
        return 'Shorten Text';
    }
  }

  labelForFontOption(option: (typeof this.fontOptions)[number]): string {
    const labels = this.translations();
    switch (option.id) {
      case 'default':
        return labels.fontDefault;
      case 'brush-script':
        return labels.fontBrushScript;
      case 'comic-sans':
        return labels.fontComicSans;
      case 'serif':
        return labels.fontSerif;
    }
  }

  labelForFontSize(id: FontSizeId): string {
    switch (id) {
      case 'small':
        return 'Small';
      case 'medium':
        return 'Medium';
      case 'large':
        return 'Large';
    }
  }

  private setNodeStyleColor(
    key: 'background-color' | 'foreground-color' | 'border-color',
    color: string | null,
  ): void {
    this.applyToSelectedNode((service, node) =>
      service.setNodeStyleColor(node.id, key, color),
    );
  }

  private isVisibleColor(color: string | null): color is string {
    return (
      typeof color === 'string' &&
      color.trim().length > 0 &&
      color.trim().toLowerCase() !== TRANSPARENT_COLOR
    );
  }

  private setDescendantLineStyle(
    style: Parameters<FlashMapService['setNodeDescendantLineStyle']>[1],
  ): void {
    this.applyToSelectedNode((service, node) =>
      service.setNodeDescendantLineStyle(node.id, style),
    );
  }

  private selectedLineConnectionData(): NodeDataRecord {
    this.styleRevision();
    const node = this.selectedNode();
    const connectionNode = node?.children?.[0] ?? node;
    return (connectionNode?.data ?? {}) as NodeDataRecord;
  }

  private resolveLineWidthId(value: unknown): LineWidthId {
    const width = typeof value === 'number' ? value : Number(value);
    if (width >= 6) {
      return 'thick';
    }
    if (width <= 2) {
      return 'thin';
    }
    return 'medium';
  }

  private applyToSelectedNode(
    action: (service: FlashMapService, node: FlashMapNodeModel) => void,
  ): void {
    const service = this.service();
    const node = this.selectedNode();
    if (!service || !node || !service.getEditable()) {
      return;
    }
    action(service, node);
    this.styleRevision.update((value) => value + 1);
    this.updatePosition();
  }

  private bindServiceEvents(service: FlashMapService | null): void {
    if (!service) {
      return;
    }

    if (service !== this.boundService) {
      this.boundService = service;
      service.addEventListener((type) => {
        if (type === EventType.SELECT) {
          this.dismissedNodeId.set(null);
        }

        if (
          type === EventType.SELECT ||
          type === EventType.RESIZE ||
          type === EventType.EDIT
        ) {
          this.updatePosition();
        }
      });
    }

    const container = service.getContainer();
    if (container && container !== this.boundContainer) {
      this.boundContainer?.removeEventListener(
        'flash-map-image-menu',
        this.imageMenuHandler,
      );
      this.boundContainer = container;
      container.addEventListener('flash-map-image-menu', this.imageMenuHandler);
    }
  }

  private startTracking(): void {
    if (this.rafId !== null) {
      return;
    }
    const tick = () => {
      this.updatePosition();
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  private stopTracking(): void {
    if (this.rafId === null) {
      return;
    }
    cancelAnimationFrame(this.rafId);
    this.rafId = null;
  }

  private updatePosition(): void {
    const node = this.selectedNode();
    const service = this.service();
    const mapElement = this.getMapElement();
    const nodeElement = this.getNodeElement(node, service);

    if (
      !node?.topic ||
      !service ||
      !mapElement ||
      !nodeElement ||
      this.isPresentationActive() ||
      this.dismissedNodeId() === node.id ||
      this.isEditing(service) ||
      !this.isMapMode()
    ) {
      this.hidePanel();
      return;
    }

    const mapRect = mapElement.getBoundingClientRect();
    const nodeRect = nodeElement.getBoundingClientRect();
    if (mapRect.width <= 0 || mapRect.height <= 0) {
      this.position.set({
        visible: true,
        left: nodeRect.left - mapRect.left,
        top: nodeRect.top - mapRect.top - 19,
        verticalPlacement: 'above',
      });
      this.resetPopoverPosition();
      return;
    }

    const panel = this.getPanelElement(mapElement);
    const panelWidth = panel?.offsetWidth ?? 0;
    const panelHeight = panel?.offsetHeight ?? 0;
    const nodeLeft = nodeRect.left - mapRect.left;
    const nodeTop = nodeRect.top - mapRect.top;
    const nodeBottom = nodeRect.bottom - mapRect.top;
    const maxLeft = Math.max(
      PANEL_EDGE_INSET,
      mapRect.width - panelWidth - PANEL_EDGE_INSET,
    );
    const canDisplayAbove =
      nodeTop - panelHeight - PANEL_NODE_GAP >= PANEL_EDGE_INSET;

    this.position.set({
      visible: true,
      left: this.clamp(nodeLeft, PANEL_EDGE_INSET, maxLeft),
      top: canDisplayAbove
        ? nodeTop - PANEL_NODE_GAP
        : nodeBottom + PANEL_NODE_GAP,
      verticalPlacement: canDisplayAbove ? 'above' : 'below',
    });
    this.updatePopoverPosition(mapElement, mapRect);
  }

  private schedulePopoverPositionUpdate(): void {
    if (this.popoverRafId !== null) {
      cancelAnimationFrame(this.popoverRafId);
    }

    this.popoverRafId = requestAnimationFrame(() => {
      this.popoverRafId = null;
      this.updatePosition();
    });
  }

  private updatePopoverPosition(
    mapElement: HTMLElement,
    mapRect: DOMRect,
  ): void {
    if (!this.activePopover()) {
      this.resetPopoverPosition();
      return;
    }

    const panel = this.getPanelElement(mapElement);
    const popover = this.getPopoverElement(panel);
    if (!panel || !popover) {
      return;
    }

    const panelRect = panel.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();
    const safeLeft = mapRect.left + PANEL_EDGE_INSET;
    const safeTop = mapRect.top + PANEL_EDGE_INSET;
    const availableWidth = Math.max(0, mapRect.width - PANEL_EDGE_INSET * 2);
    const availableHeight = Math.max(0, mapRect.height - PANEL_EDGE_INSET * 2);
    const popoverWidth = Math.min(popoverRect.width, availableWidth);
    const popoverHeight = Math.min(popoverRect.height, availableHeight);
    const safeRight = mapRect.right - PANEL_EDGE_INSET;
    const safeBottom = mapRect.bottom - PANEL_EDGE_INSET;
    const isMobile =
      typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT;

    const preferredLeft = isMobile
      ? panelRect.left
      : panelRect.left - POPOVER_GAP - popoverWidth;
    const canUsePreferredPosition = isMobile
      ? panelRect.bottom + POPOVER_GAP + popoverHeight <= safeBottom
      : preferredLeft >= safeLeft;
    const fallbackLeft = isMobile
      ? panelRect.left
      : panelRect.right + POPOVER_GAP;
    const preferredTop = isMobile
      ? panelRect.bottom + POPOVER_GAP
      : panelRect.top;
    const fallbackTop = isMobile
      ? panelRect.top - POPOVER_GAP - popoverHeight
      : panelRect.top;
    const left = this.clamp(
      canUsePreferredPosition ? preferredLeft : fallbackLeft,
      safeLeft,
      Math.max(safeLeft, safeRight - popoverWidth),
    );
    const top = this.clamp(
      canUsePreferredPosition ? preferredTop : fallbackTop,
      safeTop,
      Math.max(safeTop, safeBottom - popoverHeight),
    );

    this.popoverPosition.set({
      left: left - panelRect.left,
      top: top - panelRect.top,
      maxHeight: availableHeight,
      maxWidth: availableWidth,
    });
    this.popoverPositioned.set(true);
  }

  private hidePanel(): void {
    this.position.set({
      visible: false,
      left: 0,
      top: 0,
      verticalPlacement: 'above',
    });
    this.resetPopoverPosition();
  }

  private resetPopoverPosition(): void {
    this.popoverPositioned.set(false);
    this.popoverPosition.set({
      left: 0,
      top: 0,
      maxHeight: null,
      maxWidth: null,
    });
  }

  private closePopover(): void {
    this.popoverPositioned.set(false);
    this.activePopover.set(null);
  }

  private getPanelElement(mapElement: HTMLElement): HTMLElement | null {
    return mapElement.querySelector<HTMLElement>('.map-node-action-panel');
  }

  private getPopoverElement(panel: HTMLElement | null): HTMLElement | null {
    return (
      panel?.querySelector<HTMLElement>(
        '.node-style-popover, .node-refine-popover',
      ) ?? null
    );
  }

  private clamp(value: number, minimum: number, maximum: number): number {
    return Math.min(Math.max(value, minimum), maximum);
  }

  private getNodeElement(
    node: FlashMapNodeModel | null,
    service: FlashMapService | null,
  ): HTMLElement | null {
    const viewData = node?._data?.view as NodeViewData | undefined;
    if (viewData?.element instanceof HTMLElement) {
      return viewData.element;
    }
    const container = service?.getContainer();
    if (!container || !node?.id) {
      return null;
    }
    return container.querySelector<HTMLElement>(
      `jmnode[nodeid="${this.escapeCssValue(node.id)}"]`,
    );
  }

  private getMapElement(): HTMLElement | null {
    const host = this.getPanelApi().flashMapService?.getContainer();
    return host?.closest('.mindmap-map') as HTMLElement | null;
  }

  private isEditing(service: FlashMapService): boolean {
    return !!service.getContainer()?.querySelector('.jsmind-editor');
  }

  private isMapMode(): boolean {
    const mode = this.getPanelApi().mode;
    return !mode || mode() === 'map';
  }

  private getPanelApi(): FlashMapPanelApi {
    return (this.flashMap() ?? {}) as FlashMapPanelApi;
  }

  private eventPathContains(event: Event, selector: string): boolean {
    const path = event.composedPath?.() ?? [];
    for (const item of path) {
      if (item instanceof Element && item.matches(selector)) {
        return true;
      }
    }

    const target = event.target instanceof Element ? event.target : null;
    return !!target?.closest(selector);
  }

  private normalizeColor(value: unknown): string | null {
    return typeof value === 'string' && value.trim()
      ? value.trim().toLowerCase()
      : null;
  }

  private normalizeFontFamily(value: string | null | undefined): string | null {
    return typeof value === 'string' && value.trim()
      ? value.trim().toLowerCase()
      : null;
  }

  private readFontFamilyFromNodeData(
    data: NodeDataRecord | null | undefined,
  ): string | null {
    const fontFamily = data?.['font-family'];
    return typeof fontFamily === 'string' && fontFamily.trim()
      ? fontFamily.trim()
      : null;
  }

  private readFontSizeFromNodeData(
    data: NodeDataRecord | null | undefined,
  ): number {
    const fontSize = data?.['font-size'];
    if (typeof fontSize === 'number' && Number.isFinite(fontSize)) {
      return fontSize;
    }

    if (typeof fontSize === 'string') {
      const parsed = Number.parseFloat(fontSize);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }

    return DEFAULT_FONT_SIZE;
  }

  private resolveFontSizeByLevel(
    level: FontSizeId,
    fontFamily: string | null,
  ): number {
    return this.isCursiveFontFamily(fontFamily)
      ? CURSIVE_FONT_SIZE_SCALE[level]
      : DEFAULT_FONT_SIZE_SCALE[level];
  }

  private resolveFontSizeLevel(
    size: number,
    fontFamily: string | null,
  ): FontSizeId {
    const scale = this.isCursiveFontFamily(fontFamily)
      ? CURSIVE_FONT_SIZE_SCALE
      : DEFAULT_FONT_SIZE_SCALE;
    const entries = Object.entries(scale) as Array<[FontSizeId, number]>;

    for (const [id, mappedSize] of entries) {
      if (mappedSize === size) {
        return id;
      }
    }

    let nearest: [FontSizeId, number] = entries[0];
    for (const candidate of entries.slice(1)) {
      if (Math.abs(candidate[1] - size) < Math.abs(nearest[1] - size)) {
        nearest = candidate;
      }
    }
    return nearest[0];
  }

  private isCursiveFontFamily(fontFamily: string | null): boolean {
    const cursiveOption = this.fontOptions.find(
      (option) => option.id === 'brush-script',
    );
    return (
      this.normalizeFontFamily(fontFamily) ===
      this.normalizeFontFamily(cursiveOption?.value ?? null)
    );
  }

  private resolveTextData(data: NodeDataRecord): NodeDataRecord['text'] | null {
    return data.text ?? data.data?.text ?? null;
  }

  private escapeCssValue(value: string): string {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      return CSS.escape(value);
    }
    return value.replace(/["\\]/g, '\\$&');
  }
}
